import {
  type CitationsData,
  isHelpHandle,
  isToolPart,
  isWebHandle,
  toolNameOf,
  WEB_SEARCH_TOOL_NAME,
  type WebCitation,
  webDomainOf,
} from "@esi/shared";
import type { CitationState } from "./citation-state.ts";
import { citationsOf } from "./citations.ts";
import { safeHref } from "./tool-approvals.ts";

/**
 * Web search on the client (openspec change `web-search`): which answers used web results, the
 * results a `web_search` call returned, and the state of a `W#` citation. Everything external
 * is read as plain text; links keep `http(s)` URLs only.
 */

/** The model-facing name of the web search tool (`tool-web_search` parts). */

/** A `W#` handle (web citation), as opposed to an `S#` passage handle. */
export { isHelpHandle, isWebHandle };

/** One result of a `web_search` call as the activity row and the preview read it. */
export interface WebResult {
  /** Its `W#` handle, when the server minted one. */
  source: string | null;
  title: string | null;
  /** A safe `http(s)` link, else null (the title is then plain text). */
  href: string | null;
  domain: string | null;
  published: string | null;
  snippet: string | null;
}

const text = (value: unknown) =>
  typeof value === "string" && value.trim() !== "" ? value.trim() : null;

/** The host name of a safe link without a leading `www.`. */
export function domainOf(href: string | null): string | null {
  if (!href) {
    return null;
  }
  return webDomainOf(href) || null;
}

/** Whether a tool part is a `web_search` call. */
export const isWebSearchPart = (part: unknown): boolean =>
  isToolPart(part) &&
  toolNameOf(part as { type: string; toolName?: unknown }) === WEB_SEARCH_TOOL_NAME;

/** The results of a successful `web_search` output (`{ external: true, results }`), else none. */
export function webResultsOf(output: unknown): WebResult[] | null {
  if (
    typeof output !== "object" ||
    output === null ||
    (output as { external?: unknown }).external !== true
  ) {
    return null;
  }
  const results = (output as { results?: unknown }).results;
  if (!Array.isArray(results)) {
    return [];
  }
  return results.flatMap((r): WebResult[] => {
    if (typeof r !== "object" || r === null) {
      return [];
    }
    const o = r as Record<string, unknown>;
    const href = safeHref(o.url);
    const source = text(o.source);
    const item: WebResult = {
      source: source && isWebHandle(source) ? source.toUpperCase() : null,
      title: text(o.title),
      href,
      // The domain is shown only for a link that is shown.
      domain: href ? (domainOf(href) ?? text(o.domain)) : null,
      published: text(o.published),
      snippet: text(o.snippet),
    };
    // A result with neither title nor link has nothing to list (the server drops those).
    return item.title || item.href ? [item] : [];
  });
}

/**
 * Whether an answer used web results: a `web_search` call that returned results, or web
 * citations in its citations part.
 */
export function includesWebResults(parts: readonly unknown[]): boolean {
  // Asked per message on every render of a thread (each streamed chunk): cached per parts array,
  // which a message replaces rather than mutates.
  const cached = webResultsCache.get(parts);
  if (cached !== undefined) {
    return cached;
  }
  const result = computeIncludesWebResults(parts);
  webResultsCache.set(parts, result);
  return result;
}

const webResultsCache = new WeakMap<readonly unknown[], boolean>();

function computeIncludesWebResults(parts: readonly unknown[]): boolean {
  const data = citationsOf(parts as { type: string }[]);
  if (data && data.web.length > 0) {
    return true;
  }
  return parts.some(
    (part) =>
      isWebSearchPart(part) &&
      (part as { state?: unknown }).state === "output-available" &&
      (webResultsOf((part as { output?: unknown }).output)?.length ?? 0) > 0,
  );
}

/** The snippet of each `W#` handle the message's web searches returned (preview fallback). */
export function webSnippetsOf(parts: readonly unknown[]): Map<string, string> {
  const snippets = new Map<string, string>();
  for (const part of parts) {
    if (isWebSearchPart(part)) {
      for (const r of webResultsOf((part as { output?: unknown }).output) ?? []) {
        if (r.source && r.snippet) {
          snippets.set(r.source, r.snippet);
        }
      }
    }
  }
  return snippets;
}

/** What a web citation shows: like a knowledge chip, without the source-access states. */
export function webCitationState(
  citation: Pick<WebCitation, "status"> | undefined,
  data: CitationsData | null,
): CitationState {
  if (!citation || !data) {
    return "pending";
  }
  return data.state === "checking" ? "checking" : citation.status;
}
